import { Snackbar, Alert, Card, CircularProgress, Backdrop, Icon, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";
import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";
import axios from "axios.js";

function WheelParticipantsPage() {
  usePageTitle("Spin & Win - Wheel Participants");

  const navigate = useNavigate();
  const location = useLocation();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const [participants, setParticipants] = useState([]);
  const [pageLoaded, setPageLoaded] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const showMessage = (message, messageSeverity = "error") => {
    setSeverity(messageSeverity);
    setErrMsg(message || "An unexpected error occurred.");
    setOpen(true);
  };

  const handleRequestError = () => {
    setShowLoading(false);
    showMessage(
      `Unable to complete request. ${
        navigator.onLine ? "" : "Make sure you have an active internet connection."
      }`
    );
  };

  const fetchParticipants = async () => {
    if (!ref) {
      showMessage("Wheel reference is missing.");
      return;
    }

    setShowLoading(true);

    try {
      const result = await axios.get(`/portal/tm/spin/promotions/wheels/${ref}/participants.json`);
      console.log(result.data.data);
      if (result.data.status === "success") {
        const data = result.data.data.participants || [];

        setParticipants(
          data.map((row) => ({
            participant: (
              <MDBox display="flex" flexDirection="column">
                <MDTypography variant="button" fontWeight="medium">
                  {row.name || "---"}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {row.email_address || "---"}
                </MDTypography>
              </MDBox>
            ),
            phone_number: row.phone_number || "---",
            allowed_spins: row.allowed_spins !== undefined ? row.allowed_spins : 0,
            created: row.created || "---",
          }))
        );

        setPageLoaded(true);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  useEffect(() => {
    if (ref) {
      fetchParticipants();
    }
  }, [ref]);

  const tableColumns = [
    {
      Header: "Participant",
      accessor: "participant",
      align: "left",
    },
    {
      Header: "Phone Number",
      accessor: "phone_number",
      align: "left",
    },
    {
      Header: "Allowed Spins",
      accessor: "allowed_spins",
      align: "center",
    },
    {
      Header: "Registered",
      accessor: "created",
      align: "left",
    },
  ];

  const exportParticipants = async (format) => {
    if (!ref) {
      showMessage("Wheel reference is missing.");
      return;
    }

    const params = {};
    params.format = format;

    setShowLoading(true);

    try {
      const response = await axios.get(
        `/portal/tm/spin/promotions/wheels/${ref}/participants/export.json`,
        {
          params,
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type:
          format === "xlsx"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv;charset=utf-8;",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `wheel_participants.${format === "xlsx" ? "xlsx" : "csv"}`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      showMessage("Unable to export spins.");
    } finally {
      setShowLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <MDBox mt={1} mb={5}>
        <Card>
          <MDBox p={3}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              flexWrap="wrap"
              gap={2}
            >
              <MDBox>
                <MDTypography variant="h5" fontWeight="bold">
                  Wheel Participants
                </MDTypography>
                <MDTypography mt={1} variant="button" fontWeight="light" color="text">
                  View participants registered for this spin wheel.
                </MDTypography>
              </MDBox>

              <MDBox display="flex" gap={1} flexWrap="wrap">
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={() => navigate(-1)}
                  startIcon={<Icon>arrow_back</Icon>}
                  size="small"
                >
                  Back
                </MDButton>

                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={() => exportParticipants("xlsx")}
                  startIcon={<Icon>download</Icon>}
                  size="small"
                >
                  Export
                </MDButton>

                {/* <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={() => navigate(`/tm/spin/promotions/wheels/spins/${ref}`)}
                  startIcon={<Icon>casino</Icon>}
                  size="small"
                >
                  Spins
                </MDButton> */}
              </MDBox>
            </MDBox>
          </MDBox>

          {pageLoaded && (
            <>
              {participants.length > 0 ? (
                <MDBox p={0}>
                  <DataTable
                    table={{
                      columns: tableColumns,
                      rows: participants,
                    }}
                    canSearch
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries
                    buttons
                    noEndBorder
                  />
                </MDBox>
              ) : (
                <MDBox p={3}>
                  <MDTypography variant="button" color="text">
                    No participants have registered for this wheel.
                  </MDTypography>
                </MDBox>
              )}
            </>
          )}
        </Card>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default WheelParticipantsPage;
